import {graphql, HeadProps, PageProps} from 'gatsby';
import * as React from 'react';
import {useEffect, useMemo, useState} from 'react';

import ArticlePreview from '_components/ArticlePreview';
import Layout from '_components/layout';
import Select, {SelectItem} from '_components/Select';
import SEO from '_components/SEO';
import SunnyTrust from '_sections/sunnyTrust';

const ITEMS_PER_PAGE = 12;

const ProjectsPage = ({data}: PageProps<Queries.ArticlesPageQuery>) => {
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>();
  const [page, setPage] = useState(1);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const categoryId = params.get('categoryId');
    if (categoryId) {
      setSelectedCategoryId(categoryId);
    }
  }, []);

  useEffect(() => {
    const paramMap: {[key: string]: string} = {};
    if (selectedCategoryId) {
      paramMap['categoryId'] = selectedCategoryId;
    }
    if (Object.keys(paramMap).length === 0) {
      window.history.replaceState(null, '', window.location.pathname);
      return;
    }

    const params = new URLSearchParams(paramMap);
    window.history.replaceState(null, '', `?${params.toString()}`);
  }, [selectedCategoryId]);

  const categorySelectItems = useMemo(() => {
    const result: SelectItem[] = [];
    const map: {[key: string]: boolean} = {};
    data.allSanityArticle.edges.forEach(edge => {
      if (edge.node.category && !map[edge.node.category.id]) {
        result.push({
          id: edge.node.category.id,
          name: edge.node.category.name ?? '',
        });
        map[edge.node.category.id] = true;
      }
    });
    return result;
  }, [data]);

  const filteredArticles = useMemo<Queries.ArticlePreviewInfoFragment[]>(() => {
    setPage(1);

    return data.allSanityArticle.edges
      .map(edge => edge.node)
      .filter(node => {
        if (selectedCategoryId && node.category?.id !== selectedCategoryId) {
          return false;
        }
        return true;
      });
  }, [data, selectedCategoryId]);

  const visibleArticles = useMemo<Queries.ArticlePreviewInfoFragment[]>(() => {
    return filteredArticles.slice(0, page * ITEMS_PER_PAGE);
  }, [filteredArticles, page]);

  const showMore = () => {
    setPage(page + 1);
  };

  return (
    <Layout>
      <div className="max-w-section mx-auto px-4 lg:px-20 py-20">
        <div className="w-full max-w-[520px]">
          <div className="text-xl lg:text-28 font-medium text-gray1 uppercase">
            Articles
          </div>
          <div className="mt-2 text-gray1">
            Discover tips and tricks on home renovation here in the Sunny's
            articles!
          </div>
        </div>
        <div className="mt-6 z-20 relative flex">
          <div className="w-[240px]">
            <div className="block text-sm font-medium leading-6 text-gray1 uppercase">
              Category
            </div>
            <Select
              items={categorySelectItems}
              selectedId={selectedCategoryId}
              onChange={(id?: string) => setSelectedCategoryId(id)}
            />
          </div>
        </div>
        <div className="flex flex-wrap mt-8 gap-x-[85px] gap-y-10 justify-center lg:justify-start">
          {visibleArticles.map(article => (
            <ArticlePreview item={article} key={article.id} />
          ))}
        </div>
        {visibleArticles.length < filteredArticles.length ? (
          <div className="mt-20 flex justify-center">
            <button
              className="rounded-full px-6 py-2.5 bg-dark-blue text-white font-medium hover:text-primary"
              onClick={showMore}>
              Show More
            </button>
          </div>
        ) : null}
      </div>
      <div className="bg-[#f5f5f5] mt-16 lg:mt-48">
        <SunnyTrust />
      </div>
    </Layout>
  );
};

export default ProjectsPage;

export const Head = ({data}: HeadProps<Queries.ArticlesPageQuery>) => (
  <>
    <title>Articles</title>
    <SEO
      path="articles"
      sanityImage={data.sanityMetaTags?.seoImage}
      title={data.sanityMetaTags?.seoTitle}
      description={data.sanityMetaTags?.seoDescription}
    />
  </>
);

export const query = graphql`
  query ArticlesPage {
    allSanityArticle(sort: {_createdAt: DESC}) {
      edges {
        node {
          ...ArticlePreviewInfo
        }
      }
    }
    sanityMetaTags(_id: {eq: "meta-tags-articles"}) {
      _id
      seoTitle
      seoDescription
      seoImage {
        ...ImageInfo
      }
    }
  }
`;
